import { inject, Injectable } from '@angular/core';

import { TOAST_OUTLET, type ToastOutlet, type ToastRequest } from './toast-outlet';

export type NotificationTier = ToastRequest['tier'];

export interface NotificationOptions {
  /** Error toasts carry the envelope correlationId when available. */
  readonly correlationId?: string;
}

/** Auto-dismiss delay for info/success/warning; errors persist (conventions). */
export const NOTIFICATION_AUTO_DISMISS_MS = 5_000;
/** Maximum simultaneously visible toasts; overflow queues FIFO. */
export const NOTIFICATION_MAX_VISIBLE = 3;

/**
 * House notification facade (contracts/notification-conventions.md, FR-013).
 * Screens and services consume ONLY this service — the toast library lives
 * behind the TOAST_OUTLET port and is lint-banned everywhere else, so the
 * conventions are enforced by construction:
 *
 * - four tiers; info/success/warning auto-dismiss at 5 s; error persists
 * - top-right stack at --z-toast; max 3 visible; overflow queues FIFO
 * - role="status"/polite for info/success/warning; role="alert"/assertive for
 *   error; error toasts carry an accessible dismiss button
 * - never modal: no scrim, no focus theft (outlet contract)
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly outlet: ToastOutlet = inject(TOAST_OUTLET);

  private visibleCount = 0;
  private readonly pending: ToastRequest[] = [];

  show(tier: NotificationTier, message: string, options: NotificationOptions = {}): void {
    const request = this.buildRequest(tier, message, options);
    if (this.visibleCount < NOTIFICATION_MAX_VISIBLE) {
      this.dispatch(request);
    } else {
      this.pending.push(request);
    }
  }

  info(message: string, options?: NotificationOptions): void {
    this.show('info', message, options);
  }

  success(message: string, options?: NotificationOptions): void {
    this.show('success', message, options);
  }

  warning(message: string, options?: NotificationOptions): void {
    this.show('warning', message, options);
  }

  error(message: string, options?: NotificationOptions): void {
    this.show('error', message, options);
  }

  private buildRequest(
    tier: NotificationTier,
    message: string,
    options: NotificationOptions,
  ): ToastRequest {
    const isError = tier === 'error';
    return {
      tier,
      message,
      correlationId: isError ? options.correlationId : undefined,
      autoDismissMs: isError ? null : NOTIFICATION_AUTO_DISMISS_MS,
      role: isError ? 'alert' : 'status',
      ariaLive: isError ? 'assertive' : 'polite',
      requiresDismissButton: isError,
    };
  }

  private dispatch(request: ToastRequest): void {
    this.visibleCount += 1;
    this.outlet.show(request, () => {
      this.visibleCount -= 1;
      const next = this.pending.shift();
      if (next !== undefined) {
        this.dispatch(next);
      }
    });
  }
}
